import { Field, FieldProps, FormikErrors, FormikTouched } from "formik";
import React, { MouseEvent, useState } from "react";
import { FLKFormField, FLKInitialValues } from "../../../../store/onboarding/types";
import TFNConfirmationModal from "../../../OnboardingSteps/TfnForms/TFNConfirmationModal";
import ErrorMessage from "../../Tooltips/ErrorMessage";

interface Validation {
  type: string;
  params: string[];
}

interface Option {
  value: string | number;
  label: string;
  labelClass?: string;
  labelParentClass?: string;
  suggestionText?: string;
  tfnFieldName?: string;
}


interface DynamicRadioInputProps {
  name: string;
  label?: string;
  option: Option[];
  className?: string;
  error?: FormikErrors<FLKInitialValues>;
  selectedValue?: string | number;
  isValid?: boolean;
  touched?: FormikTouched<FLKInitialValues>;
  checked?: boolean;
  allData: FLKFormField;
  formik?: any; 
  setFieldValue?: (field: string, value: string | null, shouldValidate?: boolean) => void;
  setFieldError?: (field: string, message: string | undefined) => void;
  setFieldTouched?: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
  setSuperInitialValues?: (values: string | null | number) => void;
  handleClick?: (event: MouseEvent<HTMLInputElement>) => void;
  onChange?: (name: string, value: string | number) => void | ((e: React.ChangeEvent<HTMLInputElement>) => void);
  [key: string]: any; // For any additional props
  setDoTFNFieldValidation?: (value: boolean) => void;
  handleSetFormValues?: (name: string, value: string ) => void;
  setErrors?: (errors: FormikErrors<FLKInitialValues>) => void;
  setClearTFNFieldValues?: (value: boolean) => void;
}

const DynamicRadioInput: React.FC<DynamicRadioInputProps> = (props) => {
  const {
    name,
    label,
    option,
    className,
    error,
    selectedValue,
    isValid,
    touched,
    checked,
    allData,
    formik,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    setSuperInitialValues,
    handleClick,
    onChange,
    setDoTFNFieldValidation,
    handleSetFormValues,
    setErrors,
    setClearTFNFieldValues,
    ...rest
  } = props;

  let errorMessage = "";
  const [tfnFieldName, setTfnFieldName] = useState<string | null>(null);
    const [showTFNConfirmation, setShowTFNConfirmation] = useState<boolean>(false);
  const isKeyPresent = touched?.hasOwnProperty(`${name}-0`);
  // if (isKeyPresent) { // This not working when submitting form with empty value
  //   errorMessage = error?.[name] ?? "";
  // }
  errorMessage = error?.[name] ?? "";

  const fieldValidation = (value: string | null) => {
    let error;
    if (allData.validate) {
      if (allData.type === "radio-group") {
        allData?.validations.forEach((element: Validation) => {
            if (allData.type === "radio-group") {
              if (!allData.value) {
                if (element.type === "required") {
                  error = element.params[0];
                }
              }
            }
          });
      }
    }
    return error;
  };

  /**
   * Retrieves or clears the values of dynamically generated input fields based on the provided parameters.
   *
   * @param baseName - The base name used to construct the IDs of the input fields. Can be `null`.
   * @param rows - The number of rows of input fields.
   * @param columns - The number of columns of input fields.
   * @param clear - A boolean flag indicating whether to clear the input fields. Defaults to `false`.
   * @returns A concatenated string of all non-empty input field values if `clear` is `false`. Returns an empty string if `clear` is `true`.
   *
   * @remarks
   * - The function constructs the ID of each input field using the format `${baseName}-${row}-${col}`.
   * - If `clear` is `true`, the function clears the value of each input field and triggers an "input" event to update React state.
   * - If `clear` is `false`, the function collects and concatenates the values of all non-empty input fields.
   * - The function assumes that the input fields are of type `HTMLInputElement`.
   *
   * @example
   * // Example usage to retrieve values:
   * const values = getTfnFormFieldValue("field", 2, 3);
   *
   * @example
   * // Example usage to clear values:
   * getTfnFormFieldValue("field", 2, 3, true);
   */
   const getTfnFormFieldValue = (baseName: string | null, rows: number, columns: number, clear: boolean = false) => {
        const values: string[] = [];
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < columns; col++) {
                const fieldId = `${baseName}-${row}-${col}`;
                const element = document.getElementById(fieldId);
                const fieldValue = element ? (element as HTMLInputElement).value : "";
                if (fieldValue !== "") {
                    if(clear === false){
                        values.push(fieldValue);
                    }else{
                        if (element) {
                            setTimeout(() => {setFieldError && setFieldError(fieldId, "");}, 0); // Ensure the element is available
                            (element as HTMLInputElement).value = ""; // Clear the value of the input field
                            element.dispatchEvent(new Event("input", { bubbles: true })); // Trigger input event to update React state
                        }
                    }
                }
            }
        }
        return values.join("");
    };


    const clearTfnFormFieldError = (baseName: string | null, rows: number, columns: number) => {
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < columns; col++) {
                const fieldId = `${baseName}-${row}-${col}`;
                const element = document.getElementById(fieldId);
                if (element) {
                    //  setTimeout(() => {setFieldError && setFieldError(fieldId, "");}, 0);
                     setTimeout(() => { setErrors && setErrors({ [fieldId]: "" });}, 0);
                    (element as HTMLInputElement).value = ""; // Clear the value of the input field
                    element.dispatchEvent(new Event("input", { bubbles: true })); // Trigger input event to update React state
                }
            }
        }
    }


    /**
     * Handles the value of a pension form field based on the provided name.
     * 
     * @param name - The name of the form field. If null or an empty string, the field value is reset.
     * @returns The processed value of the form field.
     * 
     * @remarks
     * - If a valid `name` is provided, it retrieves the value using `getTfnFormFieldValue` and updates the state with `setTfnFieldName`.
     * - If the retrieved value has a length greater than 0, it triggers the `setShowTFNConfirmation` function to show a confirmation.
     * - If the `name` is empty or null, it resets the field name and value.
     */
    const handlePensionFormFieldValue = (name: string | null) => {
        let tfnValue = "";
         setDoTFNFieldValidation && setDoTFNFieldValidation(false);         
        if(name !== ""){
            tfnValue =  getTfnFormFieldValue && getTfnFormFieldValue(name, 3, 3);
            if(tfnValue?.length === 0){
              clearTfnFormFieldError(name, 3, 3);
            }
            setTfnFieldName(name);
            // setDoTfnFormValidation(name);
        }else{
            tfnValue = "";
            setTfnFieldName("");
            // setDoTFNFieldValidation && setDoTFNFieldValidation(false); // Once confirm no need to check TFN field validation
        }

        if(tfnValue?.length > 0){
            setShowTFNConfirmation && setShowTFNConfirmation(true);            
        }
        return tfnValue;
    };



  /**
   * Handles the confirmation of the TFN (Tax File Number) value.
   * 
   * This function performs the following actions:
   * 1. Retrieves the TFN form field value using the `getTfnFormFieldValue` function.
   * 2. Hides the TFN confirmation dialog by setting `setShowTFNConfirmation` to `false`.
   * 3. Disables TFN field validation by invoking `setDoTFNFieldValidation` with `false`, 
   *    ensuring no further validation is required after confirmation.
   */
  const onConfirmTFNValue = () => {
    // getTfnFormFieldValue(tfnFieldName, 3, 3, true);
    clearTfnFormFieldError(tfnFieldName, 3, 3);
    setShowTFNConfirmation(false);
    setDoTFNFieldValidation && setDoTFNFieldValidation(false); // Once confirm no need to check TFN field validation
  }

  /**
   * Hides the TFN (Tax File Number) value confirmation dialog.
   * 
   * This function sets the `showTFNConfirmation` state to `false`, effectively closing the dialog.
   * It also retrieves the TFN form field value using the `getTfnFormFieldValue` function, 
   * ensuring that the field is cleared if necessary.
   */
  const onHideTFNValue = () => {
    setShowTFNConfirmation(false);
    // getTfnFormFieldValue(tfnFieldName, 3, 3, true);
    // clearTfnFormFieldError(tfnFieldName, 3, 3);
    setClearTFNFieldValues && setClearTFNFieldValues(true); // Clear TFN field values
    setDoTFNFieldValidation && setDoTFNFieldValidation(true); // Once confirm no need to check TFN field validation
    handleSetFormValues && handleSetFormValues("radio-group-1691474443029-0", "");
  }


  return (
    <>
      <TFNConfirmationModal
        visible={showTFNConfirmation}
        onConfirm={onConfirmTFNValue}
        onCancel={onHideTFNValue}
      />
      <Field name={name} validate={fieldValidation} {...rest}>
        {({ field }: FieldProps) => {
          return option?.map((ress: Option, index: number) => {
            return (
              <div
                key={`${field.name}-${index}`} // Ensure unique key for each child
                className={`theme-check theme-check-tfn d-flex align-items-center gap-2 ${ress?.labelParentClass}`}
              >
                <input
                  key={`${field.name}-${index}`}
                  className="theme-check-input"
                  type="radio"
                  id={`${field.name}-${index}`}
                  {...field}
                  value={ress.value}
                  checked={selectedValue === ress.value}
                  onChange={(e) => {
                    let selectedValue = e.target.value;
                    allData.value = selectedValue;                      
                    if (props.onChange) {
                      props.onChange(name, selectedValue); // Call any custom onChange passed via props
                    }
                    field.onChange(e); // Ensure Formik's onChange is called
                    
                  }}
                  onClick={(e) => {
                    if (handleClick) handleClick(e); 
                    handlePensionFormFieldValue(ress.tfnFieldName || "");
                  }}
                  

                />
                <label
                  className={`theme-check-label fs-16 ${ress?.labelClass}`}
                  htmlFor={`${field.name}-${index}`}
                  dangerouslySetInnerHTML={{ __html: ress.label }}
                ></label>
                {ress?.suggestionText && ress?.suggestionText?.length > 0 ? (
                  <svg
                    className="min-w-9px"
                    fill="none"
                    height="39"
                    viewBox="0 0 9 39"
                    width="9"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 19.5L0.75 38.9856L0.75 0.0144291L9 19.5Z"
                      fill="black"
                    />
                  </svg>
                ) : null}
                <div
                  className="suggestion-text fs-16 fw-400 text-black ms-2"
                  dangerouslySetInnerHTML={{ __html: ress.suggestionText || "" }}
                ></div>
              </div>
            );
          });
        }}
      </Field>
      <ErrorMessage message={errorMessage} />
    </>
  );
};

export default DynamicRadioInput;
